import React from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  IconButton,
  Stack,
  Divider,
  Link,
  Button,
} from "@mui/material";
import {
  Instagram,
  Telegram,
  WhatsApp,
  LocationOn,
  Email,
  Phone,
  Send,
} from "@mui/icons-material";

export default function Footer() {
  const theme = useTheme();

  const socialLinks = [
    {
      icon: <Instagram />,
      url: "https://www.instagram.com/adinzone.ir?igsh=YnZwbXhucGtveGV2&utm_source=qr",
      label: "اینستاگرام",
      color: "#E4405F",
      gradient: "linear-gradient(135deg, #833AB4, #E4405F, #FD1D1D)",
    },
    {
      icon: <Telegram />,
      url: "https://t.me/adinzoneadmin",
      label: "تلگرام",
      color: "#26A5E4",
      gradient: "linear-gradient(135deg, #0088cc, #26A5E4)",
    },
  ];

  const menuItems = [
    {
      title: "دسترسی سریع",
      links: [
        { name: "صفحه اصلی", href: "/" },
        { name: "باشگاه‌ها", href: "/gyms" },
        { name: "مربیان", href: "/coaches" },
        { name: "همکاری", href: "/cooperation" },
      ],
    },
    {
      title: "حرفه‌ای‌ها",
      links: [
        {
          name: "پیج آدین زون",
          href: "https://www.instagram.com/adinzone?igsh=ZjllY2FpbWt3bmE4",
        },
        { name: "مربی گری", href: "https://t.me/morabigarivarzeshi" },
        { name: "دیوار ورزشی", href: "https://t.me/divarvarzshi" },
        { name: "کانال موزیک", href: "https://t.me/adinzone_music" },
      ],
    },
    {
      title: "تماس با ما",
      links: [
        {
          name: "09384728015",
          href: "tel:09384728015",
          icon: <Phone sx={{ fontSize: 14 }} />,
        },
        {
          name: "Aadinzonee@gmail.com",
          href: "mailto:Aadinzonee@gmail.com",
          icon: <Email sx={{ fontSize: 14 }} />,
        },
        // {
        //   name: "تهران، خیابان ولیعصر، پلاک ۱۲۳",
        //   href: "#",
        //   icon: <LocationOn sx={{ fontSize: 14 }} />,
        // },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "custom.contrast",
        borderTop: `1px solid ${theme.palette.divider}`,
        mt: 8,
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="xl">
        {/* Top Section - Social Media - SEXY VERSION */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            ما را در شبکه‌های اجتماعی دنبال کنید
          </Typography>

          <Stack direction="row" gap={"16px"} justifyContent="center">
            {socialLinks.map((social, index) => (
              <Button
                key={index}
                component="a"
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={social.icon}
                sx={{
                  border: `1.5px solid ${social.color}40`,
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  color: social.color,
                  fontWeight: 600,
                  gap: 1,
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    borderColor: social.color,
                    background: `linear-gradient(135deg, ${social.color}15, ${social.color}05)`,
                    transform: "translateY(-6px) scale(1.02)",
                    boxShadow: `0 8px 20px ${social.color}40`,
                  },
                  "& .MuiButton-startIcon": {
                    fontSize: 24,
                    transition: "transform 0.3s ease",
                  },
                  "&:hover .MuiButton-startIcon": {
                    transform: "rotate(10deg) scale(1.1)",
                  },
                }}
              >
                {social.label}
              </Button>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ mb: 5 }} />

        {/* Middle Section - Menu Lists */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 4, sm: 2 }}
          justifyContent="space-between"
          sx={{ mb: 5, px: { xs: "2rem", sm: "0" } }}
        >
          {menuItems.map((section, idx) => (
            <Box key={idx} sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2.5,
                  position: "relative",
                  display: "inline-block",
                  color: theme.palette.primary.main,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -6,
                    right: 0,
                    width: 30,
                    height: 2.5,
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 2,
                  },
                }}
              >
                {section.title}
              </Typography>

              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {section.links.map((link, linkIdx) => (
                  <Link
                    key={linkIdx}
                    href={link.href}
                    underline="none"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "text.secondary",
                      fontSize: "0.9rem",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      "&:hover": {
                        color: theme.palette.primary.main,
                        transform: "translateX(-4px)",
                      },
                    }}
                  >
                    {link.icon && link.icon}
                    {link.name}
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Bottom Section - Simple Copyright */}
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            © {new Date().getFullYear()} تمامی حقوق توسط برند آدین زون سلب شده
            است
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
